import React from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { isJingleMuted, setJingleMuted } from '@/utils/loginJingle';
import {
  GraduationCap, FileText, Users, LogOut, Search, Info, Shield, Volume2, VolumeX, UserCheck
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserMenu = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const [muted, setMuted] = React.useState(isJingleMuted());
  const [switchingToStudent, setSwitchingToStudent] = React.useState(false);
  const [switchingToTeacher, setSwitchingToTeacher] = React.useState(false);

  // v6.36.13 — Bypass the AuthContext global axios interceptor.
  //
  // The v6.36.12 instrumentation surfaced the real bug: AuthContext
  // registers an axios.interceptors.request hook that overwrites
  // every outgoing Authorization header with whatever is currently in
  // localStorage.mosaic_session_token. Our verify call set the new
  // student token in `headers` explicitly, but the interceptor ran
  // AFTER and overwrote it with the still-old super_admin token. So
  // /auth/me received the super_admin token and (correctly) returned
  // super_admin → verification "failed" but for an entirely
  // misleading reason. This is also why every prior fix attempt
  // silently sent the operator to Home: the interceptor was
  // overwriting their token swap on the very next request after the
  // localStorage write but before AuthContext's checkAuth could
  // re-mount.
  //
  // Fix: use a fresh axios instance (axios.create()) for the verify
  // call — fresh instances do not inherit the global interceptor.
  // AND write to localStorage BEFORE the verify so even if any other
  // request slips through during the redirect, it carries the new
  // token.
  const _runDemoSwitch = async ({
    apiPath, busySetter, successMsg, fallbackRedirect, expectedRole,
  }) => {
    if (switchingToStudent || switchingToTeacher) return;
    busySetter(true);
    try {
      // Step 1 — mint the new session via the backend.
      const r = await axios.post(`${API}${apiPath}`);
      const newToken = r?.data?.session_token;
      const redirectTo = r?.data?.redirect_to || fallbackRedirect;
      const newUser = r?.data?.user;
      if (!newToken) throw new Error('Mint endpoint did not return a session_token');
      if (!newUser || newUser.role !== expectedRole) {
        throw new Error(
          `Mint returned wrong role: expected ${expectedRole}, got ${newUser?.role}`
        );
      }

      // Step 2 — swap localStorage IMMEDIATELY so any in-flight or
      // racing request (including ours) picks up the new token.
      try { delete axios.defaults.headers.common['Authorization']; } catch (_e) {}
      localStorage.setItem('mosaic_session_token', newToken);

      // Step 3 — verify with a FRESH axios instance that bypasses the
      // global interceptor. This is the call that finally exposed the
      // v6.36.6 → v6.36.12 chain of silent failures.
      const bareClient = axios.create();
      const verifyResp = await bareClient.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${newToken}` },
      });
      if (!verifyResp?.data || verifyResp.data.role !== expectedRole) {
        throw new Error(
          `Verification failed: /auth/me returned role=${verifyResp?.data?.role} ` +
          `expected ${expectedRole}. (If you see this, the interceptor bypass broke.)`
        );
      }

      // Step 4 — preload the verified user for instant hydration on
      // the destination page.
      try {
        localStorage.setItem('mosaic_user_preload', JSON.stringify({
          user: verifyResp.data,
          minted_at: Date.now(),
        }));
      } catch (_e) {}

      // v6.36.16 — Trigger MOI mascot greeting on demo student switch.
      if (expectedRole === 'student') {
        try { sessionStorage.setItem('mosaic_pending_greeting', '1'); } catch (_e) {}
      }

      toast.success(successMsg);

      // Step 5 — hard navigate. Tiny delay so Sonner paint + storage
      // writes commit before tear-down.
      setTimeout(() => {
        window.location.replace(redirectTo);
      }, 80);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Demo switch failed: ${detail}`);
      // eslint-disable-next-line no-console
      console.error('[demo-quick-switch] FAILED:', e);
      busySetter(false);
    }
  };

  const handleQuickSwitchToStudent = () =>
    _runDemoSwitch({
      apiPath: '/demo/quick-switch-student',
      busySetter: setSwitchingToStudent,
      successMsg: 'Switched to Demo Student — presentation mode',
      fallbackRedirect: '/',
      expectedRole: 'student',
    });

  const handleQuickSwitchToTeacher = () =>
    _runDemoSwitch({
      apiPath: '/demo/quick-switch-teacher',
      busySetter: setSwitchingToTeacher,
      successMsg: 'Switched to Demo Teacher — presentation mode',
      fallbackRedirect: '/teacher',
      expectedRole: 'teacher',
    });

  if (!isAuthenticated) {
    return (
      <Link to="/login">
        <Button variant="outline" className="btn-sharp h-10">
          Sign In
        </Button>
      </Link>
    );
  }
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center gap-2 h-10" data-tour="user-menu">
          <div className="w-8 h-8 bg-[#002FA7] text-white rounded-full flex items-center justify-center text-sm font-semibold">
            {user?.name?.charAt(0) || 'U'}
          </div>
          <span className="hidden md:inline">{user?.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium">{user?.name}</p>
          <p className="text-xs text-gray-500">{user?.email}</p>
          <p className="text-xs text-[#002FA7] mt-1 capitalize">{user?.role?.replace('_', ' ')}</p>
        </div>
        <DropdownMenuSeparator />
        {(user?.role === 'teacher' || user?.role === 'school_admin' || user?.role === 'district_admin' || user?.role === 'super_admin') && (
          <DropdownMenuItem asChild>
            <Link to="/teacher" className="cursor-pointer">
              <GraduationCap className="mr-2" size={16} />
              Teacher Dashboard
            </Link>
          </DropdownMenuItem>
        )}
        {user?.role === 'school_admin' && (
          <DropdownMenuItem asChild>
            <Link to="/admin/school" className="cursor-pointer">
              <Users className="mr-2" size={16} />
              School Dashboard
            </Link>
          </DropdownMenuItem>
        )}
        {(user?.role === 'district_admin' || user?.role === 'super_admin') && (
          <DropdownMenuItem asChild>
            <Link to="/admin/district" className="cursor-pointer">
              <Users className="mr-2" size={16} />
              District Dashboard
            </Link>
          </DropdownMenuItem>
        )}
        {user?.role === 'super_admin' && (
          <DropdownMenuItem asChild>
            <Link to="/superadmin" className="cursor-pointer" data-testid="nav-superadmin">
              <Shield className="mr-2" size={16} />
              Super Admin
            </Link>
          </DropdownMenuItem>
        )}
        {/* v6.36.6 — One-click Demo Student switch for live presentations. */}
        {(user?.role === 'super_admin' || user?.role === 'district_admin') && (
          <DropdownMenuItem
            onClick={handleQuickSwitchToStudent}
            disabled={switchingToStudent || switchingToTeacher}
            className="cursor-pointer text-rose-700 focus:text-rose-800 focus:bg-rose-50"
            data-testid="nav-demo-student-switch"
          >
            <UserCheck className="mr-2" size={16} />
            {switchingToStudent ? 'Switching…' : 'Student Dashboard (Demo)'}
          </DropdownMenuItem>
        )}
        {/* v6.36.9 — One-click Demo Teacher switch for live presentations. */}
        {(user?.role === 'super_admin' || user?.role === 'district_admin') && (
          <DropdownMenuItem
            onClick={handleQuickSwitchToTeacher}
            disabled={switchingToStudent || switchingToTeacher}
            className="cursor-pointer text-rose-700 focus:text-rose-800 focus:bg-rose-50"
            data-testid="nav-demo-teacher-switch"
          >
            <GraduationCap className="mr-2" size={16} />
            {switchingToTeacher ? 'Switching…' : 'Teacher Dashboard (Demo)'}
          </DropdownMenuItem>
        )}
        {(user?.role === 'teacher' || user?.role === 'school_admin' || user?.role === 'district_admin' || user?.role === 'super_admin') && (
          <DropdownMenuSeparator />
        )}
        {user?.role === 'student' && (
          <>
            <DropdownMenuItem asChild>
              <Link to="/my-feedback" className="cursor-pointer">
                <FileText className="mr-2" size={16} />
                My Feedback
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/my-progress" className="cursor-pointer">
                <Search className="mr-2" size={16} />
                My Progress
              </Link>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/guide" className="cursor-pointer">
            <Info className="mr-2" size={16} />
            User Guide
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => { const next = !muted; setMuted(next); setJingleMuted(next); }} className="cursor-pointer" data-testid="jingle-toggle">
          {muted ? <VolumeX className="mr-2" size={16} /> : <Volume2 className="mr-2" size={16} />}
          Login Sound {muted ? "Off" : "On"}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600">
          <LogOut className="mr-2" size={16} />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
